import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Users, CheckCircle,
  Eye, RotateCcw, BookOpen, Plus, ChevronDown, ChevronRight, Search, Filter, Shield, Database
} from 'lucide-react';
import {
  getExamSet,
  getExamSessions,
  resetExamSession,
  publishExamSet
} from '../lib/examApi';
import { getCourseInstances, type CourseInstance } from '../lib/admin';
import { getUser } from '../lib/auth';
import { useToast, useConfirm } from './ui';
import type { ExamSet, ExamQuestion, ExamSession } from '../types/exam';

const ExamDetail: React.FC = () => {
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [examSet, setExamSet] = useState<ExamSet | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [sessions, setSessions] = useState<(ExamSession & {
    percentage?: number;
    grade?: string;
    passed?: boolean;
  })[]>([]);
  const [courseInstances, setCourseInstances] = useState<CourseInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'questions' | 'sessions' | 'courses'>('overview');
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const user = getUser();
    setCurrentUser(user);

    if (id) {
      loadExamData();
    }
  }, [id]);

  const loadExamData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [examData, sessionsData, coursesData] = await Promise.all([
        getExamSet(id),
        getExamSessions(id),
        getCourseInstances({ exam_set_id: id })
      ]);

      setExamSet(examData.exam_set);
      setQuestions(examData.questions);
      setSessions(sessionsData);
      setCourseInstances(coursesData);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!id || !examSet) return;

    const confirmed = await confirm({
      title: 'Publiser prøvesett',
      message: `Publiser prøvesettet "${examSet.title}"? Dette gjør det tilgjengelig for kandidater.`,
      confirmLabel: 'Publiser',
      cancelLabel: 'Avbryt'
    });

    if (!confirmed) return;

    try {
      const publicLink = await publishExamSet(id);
      const fullUrl = `${window.location.origin}/exam/${publicLink}`;

      // Copy to clipboard
      navigator.clipboard.writeText(fullUrl);

      toast.showSuccess(`Prøven er publisert! Lenke kopiert til utklippstavlen.`);
      await loadExamData();
    } catch (err: any) {
      toast.showError('Feil ved publisering: ' + err.message);
    }
  };

  const handleResetSession = async (sessionId: string, anonymousId: string) => {
    const confirmed = await confirm({
      title: 'Tilbakestill prøve',
      message: `Tilbakestill prøven for kandidat "${anonymousId}"? Dette lar kandidaten ta prøven på nytt.`,
      confirmLabel: 'Tilbakestill',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      await resetExamSession(sessionId);
      toast.showSuccess('Prøven er tilbakestilt');
      await loadExamData();
    } catch (err: any) {
      toast.showError('Kunne ikke tilbakestille: ' + err.message);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800'
    };

    const labels = {
      draft: 'Utkast',
      published: 'Publisert',
      archived: 'Arkivert'
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const getSessionStatusBadge = (status: string) => {
    const styles = {
      in_progress: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      graded: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      reset: 'bg-gray-100 text-gray-800'
    };

    const labels = {
      in_progress: 'Pågår',
      submitted: 'Innlevert',
      graded: 'Karaktersatt',
      expired: 'Utløpt',
      reset: 'Tilbakestilt'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const getGradingScaleLabel = (scale: string) => {
    const labels: Record<string, string> = {
      pass_fail: 'Bestått/Ikke bestått',
      three_tier: 'Meget godt bestått/Bestått/Ikke bestått',
      a_f: 'A-F',
      percentage: 'Prosent'
    };
    return labels[scale] || scale;
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      multiple_choice: 'Flervalg',
      written: 'Skriftlig',
      true_false: 'Sant/Usant'
    };
    return labels[type] || type;
  };

  // Access control: Only member, admin, and systemadmin can reset sessions
  const canResetSessions = currentUser?.role === 'member' ||
                           currentUser?.role === 'admin' ||
                           currentUser?.role === 'systemadmin';

  // Group sessions by course instance
  const groupSessionsByCourse = () => {
    const grouped: Record<string, typeof sessions> = {};
    const uncategorized: typeof sessions = [];

    sessions.forEach(session => {
      if (session.course_instance_id) {
        if (!grouped[session.course_instance_id]) {
          grouped[session.course_instance_id] = [];
        }
        grouped[session.course_instance_id].push(session);
      } else {
        uncategorized.push(session);
      }
    });

    return { grouped, uncategorized };
  };

  // Toggle course expansion
  const toggleCourse = (courseId: string) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(courseId)) {
      newExpanded.delete(courseId);
    } else {
      newExpanded.add(courseId);
    }
    setExpandedCourses(newExpanded);
  };

  // Filter sessions
  const filterSessions = (sessionsList: typeof sessions) => {
    return sessionsList.filter(session => {
      // Status filter
      if (statusFilter !== 'all' && session.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          session.anonymous_id?.toLowerCase().includes(searchLower) ||
          session.unique_candidate_id?.toLowerCase().includes(searchLower) ||
          session.candidate_code?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Laster prøvedetaljer...</div>
      </div>
    );
  }

  if (error || !examSet) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Feil ved lasting</h3>
          <p className="text-red-600 mt-1">{error || 'Kunne ikke laste prøve'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-2 text-sm text-red-700 underline"
          >
            Gå tilbake
          </button>
        </div>
      </div>
    );
  }

  // Calculate question count - either from question bank config or fixed questions
  const questionCount = examSet.use_question_bank ? (examSet.question_count || 20) : questions.length;
  const totalPoints = examSet.use_question_bank ? questionCount : questions.reduce((sum, q) => sum + Number(q.points), 0);
  const passedSessions = sessions.filter(s => s.passed).length;
  const failedSessions = sessions.filter(s => s.status === 'graded' && !s.passed).length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/exams')}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-900">{examSet.title}</h1>
            {getStatusBadge(examSet.status)}
          </div>
          {examSet.description && (
            <p className="text-gray-600">{examSet.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          {examSet.status === 'draft' && (
            <button
              onClick={handlePublish}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <CheckCircle size={20} />
              Publiser
            </button>
          )}
          <button
            onClick={() => navigate(`/exams/edit/${id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Edit size={20} />
            Rediger
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 flex items-center gap-1">
            Spørsmål
            {examSet.use_question_bank && <Database size={14} className="text-purple-600" />}
          </div>
          <div className={`text-2xl font-bold ${examSet.use_question_bank ? 'text-purple-600' : 'text-gray-900'}`}>
            {questionCount}
          </div>
          <div className="text-xs text-gray-500">
            {examSet.use_question_bank ? 'Fra spørsmålsbank' : `Totalt ${totalPoints} poeng`}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Forsøk</div>
          <div className="text-2xl font-bold text-blue-600">{sessions.length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Bestått</div>
          <div className="text-2xl font-bold text-green-600">{passedSessions}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Ikke bestått</div>
          <div className="text-2xl font-bold text-red-600">{failedSessions}</div>
        </div>
      </div>

      {/* Info: Course instances are now used for candidate registration */}
      {examSet.status === 'published' && courseInstances.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="text-yellow-600">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-yellow-900">Opprett kursinstans for å registrere kandidater</div>
              <div className="text-sm text-yellow-700 mt-1">
                Kandidater må nå registreres via kursinstanser. Gå til "Kursinstanser"-fanen for å opprette et kurs.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 border-b-2 font-medium transition ${
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Oversikt
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`pb-4 border-b-2 font-medium transition ${
              activeTab === 'questions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Spørsmål ({questionCount})
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`pb-4 border-b-2 font-medium transition ${
              activeTab === 'courses'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <BookOpen size={16} />
              Kursinstanser ({courseInstances.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`pb-4 border-b-2 font-medium transition ${
              activeTab === 'sessions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Kandidater ({sessions.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Prøveinnstillinger</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-600">Studieplan</dt>
              <dd className="font-medium text-gray-900">{examSet.study_plan_title}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Tidsbegrensning</dt>
              <dd className="font-medium text-gray-900">
                {examSet.time_limit_minutes ? `${examSet.time_limit_minutes} minutter` : 'Ingen grense'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Grense for bestått</dt>
              <dd className="font-medium text-gray-900">{examSet.pass_threshold}%</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Karakterskala</dt>
              <dd className="font-medium text-gray-900">{getGradingScaleLabel(examSet.grading_scale)}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Gyldighetsperiode</dt>
              <dd className="font-medium text-gray-900">
                {examSet.validity_years ? `${examSet.validity_years} år` : 'Evig gyldig'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Opprettet</dt>
              <dd className="font-medium text-gray-900">
                {new Date(examSet.created_at).toLocaleDateString('nb-NO')}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 flex items-center gap-1">
                <Shield size={14} />
                Safe Exam Browser
              </dt>
              <dd className="font-medium">
                {examSet.seb_required !== false ? (
                  <span className="text-blue-700">Påkrevd</span>
                ) : (
                  <span className="text-gray-500">Ikke påkrevd</span>
                )}
              </dd>
            </div>
            {examSet.use_question_bank && (
              <div className="md:col-span-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <dt className="text-sm text-purple-700 font-medium flex items-center gap-2 mb-2">
                  <Database size={16} />
                  Spørsmålsbank aktivert
                </dt>
                <dd className="text-sm text-purple-900">
                  {examSet.question_count || 20} tilfeldige spørsmål velges fra banken per kandidat
                  {examSet.difficulty_distribution && (
                    <span className="block text-xs text-purple-600 mt-1">
                      Fordeling: {Math.round((examSet.difficulty_distribution.easy || 0.2) * 100)}% enkle, {' '}
                      {Math.round((examSet.difficulty_distribution.medium || 0.6) * 100)}% middels, {' '}
                      {Math.round((examSet.difficulty_distribution.hard || 0.2) * 100)}% vanskelige
                    </span>
                  )}
                </dd>
              </div>
            )}
          </dl>

          {examSet.seb_required !== false && examSet.seb_config_key_hash && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 text-sm">
                <Shield size={16} className="text-blue-600" />
                <span className="text-blue-900 font-medium">SEB Config Key:</span>
                <code className="text-xs bg-white px-2 py-0.5 rounded border border-blue-200 text-blue-800 font-mono">
                  {examSet.seb_config_key_hash.substring(0, 16)}...
                </code>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'questions' && (
        <div className="space-y-4">
          {examSet.use_question_bank && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
              <Database size={48} className="mx-auto text-purple-400 mb-4" />
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Spørsmålsbank aktiv</h3>
              <p className="text-purple-700 mb-4">
                Dette prøvesettet bruker spørsmålsbank. {examSet.question_count || 20} tilfeldige spørsmål velges fra banken for hver kandidat.
              </p>
              <button
                onClick={() => navigate(`/exams/edit/${id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Edit size={16} />
                Administrer spørsmålsbank
              </button>
            </div>
          )}
          {!examSet.use_question_bank && questions.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="text-gray-600">Ingen spørsmål er lagt til ennå.</p>
              <button
                onClick={() => navigate(`/exams/edit/${id}`)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Edit size={16} />
                Legg til spørsmål
              </button>
            </div>
          )}
          {!examSet.use_question_bank && questions.map((question, index) => (
            <div key={question.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-gray-900">#{index + 1}</span>
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {getQuestionTypeLabel(question.question_type)}
                  </span>
                  <span className="text-sm text-gray-600">{Number(question.points)} poeng</span>
                </div>
              </div>
              <p className="text-gray-900 mb-3">{question.question_text}</p>

              {(question.question_type === 'multiple_choice' || question.question_type === 'true_false') && question.options && (
                <div className="space-y-2 mt-4">
                  {question.options.map((opt, i) => (
                    <div
                      key={i}
                      className={`p-3 rounded border ${
                        opt.is_correct ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{opt.text}</span>
                        {opt.is_correct && (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-600 text-white">
                            Riktig
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {question.question_type === 'written' && question.grading_criteria && (
                <div className="mt-4 bg-blue-50 rounded p-3">
                  <div className="text-sm font-medium text-blue-900 mb-1">Vurderingskriterier:</div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{question.grading_criteria}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'sessions' && (() => {
        const { grouped, uncategorized } = groupSessionsByCourse();
        const allFiltered = filterSessions(sessions);

        return (
          <div className="space-y-4">
            {/* Search and Filter Bar */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Søk etter kandidat-ID, kode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={18} className="text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">Alle statuser</option>
                    <option value="in_progress">Pågår</option>
                    <option value="submitted">Innlevert</option>
                    <option value="graded">Karaktersatt</option>
                    <option value="expired">Utløpt</option>
                  </select>
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Viser {allFiltered.length} av {sessions.length} kandidater
              </div>
            </div>

            {sessions.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Users size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-600">Ingen kandidater har tatt denne prøven ennå</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Grouped by Course Instance */}
                {Object.entries(grouped).map(([courseId, courseSessions]) => {
                  const course = courseInstances.find(c => c.id === courseId);
                  const filteredCourseSessions = filterSessions(courseSessions);

                  if (filteredCourseSessions.length === 0 && (searchTerm || statusFilter !== 'all')) {
                    return null; // Hide empty groups when filtering
                  }

                  const isExpanded = expandedCourses.has(courseId);
                  const submittedCount = filteredCourseSessions.filter(s => s.status === 'submitted').length;
                  const gradedCount = filteredCourseSessions.filter(s => s.status === 'graded').length;
                  const inProgressCount = filteredCourseSessions.filter(s => s.status === 'in_progress').length;

                  return (
                    <div key={courseId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      {/* Course Header */}
                      <div
                        onClick={() => toggleCourse(courseId)}
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                          <BookOpen className="text-blue-600" size={20} />
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {course?.name || `Kursinstans ${courseId.slice(0, 8)}...`}
                            </h3>
                            {course?.start_date && (
                              <p className="text-sm text-gray-500">
                                {new Date(course.start_date).toLocaleDateString('nb-NO')}
                                {course.end_date && ` - ${new Date(course.end_date).toLocaleDateString('nb-NO')}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Users size={16} className="text-gray-400" />
                            <span className="text-gray-600">{filteredCourseSessions.length} kandidater</span>
                          </div>
                          {submittedCount > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                              {submittedCount} innlevert
                            </span>
                          )}
                          {gradedCount > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                              {gradedCount} sensurert
                            </span>
                          )}
                          {inProgressCount > 0 && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                              {inProgressCount} pågår
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Candidate Table */}
                      {isExpanded && (
                        <div className="border-t border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Kandidat
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Resultat
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Startet
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                  Handlinger
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredCourseSessions.map((session) => (
                                <tr key={session.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{session.anonymous_id}</div>
                                    {session.candidate_code && (
                                      <div className="text-xs text-gray-500">Kode: {session.candidate_code}</div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {getSessionStatusBadge(session.status)}
                                  </td>
                                  <td className="px-6 py-4">
                                    {session.status === 'graded' && session.percentage !== undefined ? (
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {session.percentage}% - {session.grade}
                                        </div>
                                        <div className={`text-xs ${session.passed ? 'text-green-600' : 'text-red-600'}`}>
                                          {session.passed ? 'Bestått' : 'Ikke bestått'}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500">
                                    {new Date(session.started_at).toLocaleString('nb-NO')}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end gap-2">
                                      {(session.status === 'submitted' || session.status === 'graded') && (
                                        <button
                                          onClick={() => navigate(`/exams/review/${session.id}`)}
                                          className="text-blue-600 hover:text-blue-900"
                                          title="Vurder"
                                        >
                                          <Eye size={18} />
                                        </button>
                                      )}
                                      {canResetSessions && (session.status === 'submitted' || session.status === 'graded') && (
                                        <button
                                          onClick={() => handleResetSession(session.id, session.anonymous_id)}
                                          className="text-orange-600 hover:text-orange-900"
                                          title="Tilbakestill"
                                        >
                                          <RotateCcw size={18} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Uncategorized Sessions */}
                {uncategorized.length > 0 && (() => {
                  const filteredUncategorized = filterSessions(uncategorized);
                  if (filteredUncategorized.length === 0 && (searchTerm || statusFilter !== 'all')) {
                    return null;
                  }

                  const isExpanded = expandedCourses.has('uncategorized');

                  return (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div
                        onClick={() => toggleCourse('uncategorized')}
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                          <Users className="text-gray-400" size={20} />
                          <div>
                            <h3 className="font-semibold text-gray-900">Ukategoriserte kandidater</h3>
                            <p className="text-sm text-gray-500">Kandidater uten kursinstans</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Users size={16} className="text-gray-400" />
                          <span className="text-gray-600">{filteredUncategorized.length} kandidater</span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Kandidat
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Resultat
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Startet
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                  Handlinger
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredUncategorized.map((session) => (
                                <tr key={session.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{session.anonymous_id}</div>
                                    {session.candidate_code && (
                                      <div className="text-xs text-gray-500">Kode: {session.candidate_code}</div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {getSessionStatusBadge(session.status)}
                                  </td>
                                  <td className="px-6 py-4">
                                    {session.status === 'graded' && session.percentage !== undefined ? (
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {session.percentage}% - {session.grade}
                                        </div>
                                        <div className={`text-xs ${session.passed ? 'text-green-600' : 'text-red-600'}`}>
                                          {session.passed ? 'Bestått' : 'Ikke bestått'}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500">
                                    {new Date(session.started_at).toLocaleString('nb-NO')}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end gap-2">
                                      {(session.status === 'submitted' || session.status === 'graded') && (
                                        <button
                                          onClick={() => navigate(`/exams/review/${session.id}`)}
                                          className="text-blue-600 hover:text-blue-900"
                                          title="Vurder"
                                        >
                                          <Eye size={18} />
                                        </button>
                                      )}
                                      {canResetSessions && (session.status === 'submitted' || session.status === 'graded') && (
                                        <button
                                          onClick={() => handleResetSession(session.id, session.anonymous_id)}
                                          className="text-orange-600 hover:text-orange-900"
                                          title="Tilbakestill"
                                        >
                                          <RotateCcw size={18} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'courses' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Kursinstanser</h3>
            <button
              onClick={() => navigate(`/course-instances?exam_set_id=${id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-[#B5651D] text-white rounded-lg hover:bg-[#8B4513] transition"
            >
              <Plus size={16} />
              Opprett Kursinstans
            </button>
          </div>

          {courseInstances.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 mb-4">Ingen kursinstanser opprettet ennå</p>
              <button
                onClick={() => navigate(`/course-instances?exam_set_id=${id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#B5651D] text-white rounded-lg hover:bg-[#8B4513] transition"
              >
                <Plus size={16} />
                Opprett første kursinstans
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kursnavn
                    </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Kurskode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Periode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Kandidater
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sensorer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {courseInstances.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{course.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {course.course_code || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {course.start_date && course.end_date ? (
                        <>
                          {new Date(course.start_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })}
                          {' - '}
                          {new Date(course.end_date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {course.candidate_count || 0} / {course.max_candidates || '∞'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                        {course.sensor_count || 0} sensor{(course.sensor_count || 0) !== 1 ? 'er' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        course.status === 'active' ? 'bg-green-100 text-green-800' :
                        course.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {course.status === 'active' ? 'Aktiv' :
                         course.status === 'completed' ? 'Fullført' :
                         'Planlagt'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/course-instances/${course.id}`)}
                        className="text-[#B5651D] hover:text-[#8B4513]"
                      >
                        Se detaljer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExamDetail;
